/**
 * Catches a render error and shows it, instead of React unmounting the tree.
 *
 * Without one, any throw below the root blanks the page — the app appears, runs
 * for a moment, and vanishes with nothing on screen to say why. That failure
 * mode cost real time to diagnose, and the information needed was always there;
 * it just had nowhere to go.
 *
 * Deliberately not a retry button. Re-rendering the same state hits the same
 * throw, and offering it invites the same click twice — reload once the cause is
 * fixed. A class component because that is still the only way to implement
 * `componentDidCatch`.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Named in the message, so a nested boundary says which part failed. */
  where?: string;
}

interface State {
  error: Error | null;
  stack: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, stack: "" };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged as well as shown: the component stack says *where*, which the
    // message alone does not, and the console keeps it after a reload.
    // eslint-disable-next-line no-console
    console.error("[qufolio] render failed", error, info.componentStack);
    this.setState({ stack: info.componentStack ?? "" });
  }

  override render(): ReactNode {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={panel}>
        <strong style={{ color: "var(--qf-negative)" }}>
          {this.props.where ? `${this.props.where} failed` : "Something failed to render"}
        </strong>
        <pre style={message}>{error.message}</pre>
        {stack && (
          <details>
            <summary style={{ cursor: "pointer", color: "var(--qf-text-muted)" }}>
              Component stack
            </summary>
            <pre style={{ ...message, maxHeight: "12rem" }}>{stack.trim()}</pre>
          </details>
        )}
      </div>
    );
  }
}

const panel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  height: "100%",
  overflow: "auto",
  padding: "1rem",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  fontSize: "0.82rem",
};

const message: React.CSSProperties = {
  margin: 0,
  padding: "0.5rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.74rem",
  whiteSpace: "pre-wrap",
  overflowX: "auto",
};
